// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/HeroSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiUpload, FiX } from 'react-icons/fi';
import api from '../../../api';

const HeroSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const hero = websiteData?.hero || {};

  // Local state vectors to completely isolate text fields during live input typing
  const [localTitle, setLocalTitle] = useState(hero.title || '');
  const [localSubtitle, setLocalSubtitle] = useState(hero.subtitle || '');
  const [localDescription, setLocalDescription] = useState(hero.description || '');
  const [localCtaText, setLocalCtaText] = useState(hero.ctaText || 'Explore Programs');
  const [localCtaLink, setLocalCtaLink] = useState(hero.ctaLink || '/programs');
  const [localSecondaryCtaText, setLocalSecondaryCtaText] = useState(hero.secondaryCtaText || 'Contact Admissions');
  const [localSecondaryCtaLink, setLocalSecondaryCtaLink] = useState(hero.secondaryCtaLink || '/contact');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(hero.displayStyle || 'default');

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync isolated fields values back from props only on clean initial mount or tenant shifting steps
  useEffect(() => {
    console.log('[HeroSection] Syncing isolated input fields allocations with baseline configuration values.');
    setLocalTitle(hero.title || '');
    setLocalSubtitle(hero.subtitle || '');
    setLocalDescription(hero.description || '');
    setLocalCtaText(hero.ctaText || 'Explore Programs');
    setLocalCtaLink(hero.ctaLink || '/programs');
    setLocalSecondaryCtaText(hero.secondaryCtaText || 'Contact Admissions');
    setLocalSecondaryCtaLink(hero.secondaryCtaLink || '/contact');
    setLocalDisplayStyle(hero.displayStyle || 'default');
  }, [hero.title, hero.subtitle, hero.description, hero.ctaText, hero.ctaLink, hero.secondaryCtaText, hero.secondaryCtaLink, hero.displayStyle, tenantId]);

  const handleToggle = () => {
    handleInputChange('hero', 'isActive', !hero.isActive);
  };

  const handleLocalFieldChange = (field, value) => {
    console.log(`[HeroSection] Typing changes managed locally: Key [${field}]`);
    if (field === 'title') setLocalTitle(value);
    if (field === 'subtitle') setLocalSubtitle(value);
    if (field === 'description') setLocalDescription(value);
    if (field === 'ctaText') setLocalCtaText(value);
    if (field === 'ctaLink') setLocalCtaLink(value);
    if (field === 'secondaryCtaText') setLocalSecondaryCtaText(value);
    if (field === 'secondaryCtaLink') setLocalSecondaryCtaLink(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleBackgroundUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      event.target.value = '';
      return;
    }

    // Set immediate client-side placeholder object preview context to keep ui instant
    const localBlobPreview = URL.createObjectURL(file);
    handleInputChange('hero', 'backgroundImage', localBlobPreview);

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('backgroundImage', file);
      
      // Inject internal decoupled parameters matching latest local snapshot fields allocations
      formData.append('title', localTitle);
      formData.append('subtitle', localSubtitle);
      formData.append('description', localDescription);
      formData.append('ctaText', localCtaText);
      formData.append('ctaLink', localCtaLink);
      formData.append('secondaryCtaText', localSecondaryCtaText);
      formData.append('secondaryCtaLink', localSecondaryCtaLink);
      formData.append('displayStyle', localDisplayStyle);
      formData.append('isActive', hero.isActive !== false ? 'true' : 'false');

      const response = await api.put(
        `/api/website/${tenantId}/hero`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        console.log('[HeroSection] Server background asset synchronized.');
        handleInputChange('hero', null, response.data.data);
      } else {
        alert(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload background image');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (typeof urlPath === 'object') {
      if (urlPath.url) return urlPath.url.startsWith('/') ? (base ? `${base}${urlPath.url}` : urlPath.url) : urlPath.url;
      if (urlPath.fileId) return base ? `${base}/api/files/tenant-website/${urlPath.fileId}` : `/api/files/tenant-website/${urlPath.fileId}`;
      return '';
    }
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const handleRemoveBackground = () => {
    handleInputChange('hero', 'backgroundImage', '');
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[HeroSection] Moving localized components metrics into core global props variables blocks.');
    
    try {
      // Direct update of parent mapping elements context objects parameters map snapshots
      await handleInputChange('hero', 'title', localTitle);
      await handleInputChange('hero', 'subtitle', localSubtitle);
      await handleInputChange('hero', 'description', localDescription);
      await handleInputChange('hero', 'ctaText', localCtaText);
      await handleInputChange('hero', 'ctaLink', localCtaLink);
      await handleInputChange('hero', 'secondaryCtaText', localSecondaryCtaText);
      await handleInputChange('hero', 'secondaryCtaLink', localSecondaryCtaLink);
      await handleInputChange('hero', 'displayStyle', localDisplayStyle);

      const assembledPayload = {
        ...hero,
        title: localTitle,
        subtitle: localSubtitle,
        description: localDescription,
        ctaText: localCtaText,
        ctaLink: localCtaLink,
        secondaryCtaText: localSecondaryCtaText,
        secondaryCtaLink: localSecondaryCtaLink,
        displayStyle: localDisplayStyle
      };

      await handleSave('hero', assembledPayload);
    } catch (error) {
      console.error('Failed to save hero section:', error);
    } finally {
      setSaving(false);
    }
  };

  const resolvedBgImage = resolveAssetUrl(hero.backgroundImage);

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Action Toolbar Header Container */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Hero Section</h2>
            <p className="text-sm text-slate-500 mt-0.5">Configure the main background banner slider layout of your site appearance</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              onClick={handleToggle}
              type="button"
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                hero.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {hero.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              onClick={handleSaveClick}
              disabled={saving}
              type="button"
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Background Canvas Upload Wrapper Component layout block */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Wallpaper</h3>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            
            <div className="flex-shrink-0 w-full lg:w-64 mx-auto lg:mx-0">
              <div className="w-full h-36 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden bg-slate-100 relative shadow-inner">
                {resolvedBgImage ? (
                  <>
                    <img src={resolvedBgImage} alt="Hero Canvas Background" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={handleRemoveBackground}
                      className="absolute top-2 right-2 p-1.5 bg-rose-600 text-white rounded-full hover:bg-rose-700 shadow shadow-rose-900/40 transition-colors"
                    >
                      <FiX className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : (
                  <div className="text-center p-4">
                    <FiUpload className="mx-auto h-6 w-6 text-slate-400 mb-1" />
                    <span className="text-xs font-semibold text-slate-400">Empty backdrop</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 text-center lg:text-left">
              <input
                type="file"
                id="hero-background-upload"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleBackgroundUpload}
                disabled={uploading}
                className="hidden"
              />
              <label
                htmlFor="hero-background-upload"
                className={`inline-flex items-center px-4 py-2 border border-slate-200 rounded-lg shadow-sm text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 cursor-pointer transition-all select-none active:scale-95 ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? 'Uploading...' : (resolvedBgImage ? 'Change Wallpaper Image' : 'Upload Wallpaper Image')}
              </label>
              <p className="mt-2 text-[10px] text-slate-400 leading-normal">Recommended dimensions: 1920x1080 resolution bounds, max file scope size 2MB (JPG, PNG, WEBP)</p>
            </div>
          </div>
        </div>

        {/* Content Configuration Form block alignment */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Text Content Elements</h3>
          
          <div className="space-y-3.5">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Hero Title Headline Header</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleLocalFieldChange('title', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Welcome to Our Institution"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Secondary Subtitle Description Row</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleLocalFieldChange('subtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Where Excellence Meets Innovation"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Core Description Abstract</label>
              <textarea
                value={localDescription}
                onChange={(e) => handleLocalFieldChange('description', e.target.value)}
                rows="3"
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none resize-y"
                placeholder="Brief description summary layout of your campus facilities and parameters..."
              />
            </div>
          </div>
        </div>

        {/* Primary CTA Fields Alignment setup */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Primary Actions Call (CTA)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Button Display Label Text</label>
              <input
                type="text"
                value={localCtaText}
                onChange={(e) => handleLocalFieldChange('ctaText', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Explore Programs"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Button Destination Anchor Link</label>
              <input
                type="text"
                value={localCtaLink}
                onChange={(e) => handleLocalFieldChange('ctaLink', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="/programs"
              />
            </div>
          </div>
        </div>

        {/* Secondary CTA Fields Alignment setup */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Secondary Actions Call (CTA)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Button Display Label Text</label>
              <input
                type="text"
                value={localSecondaryCtaText}
                onChange={(e) => handleLocalFieldChange('secondaryCtaText', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Contact Admissions"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Button Destination Anchor Link</label>
              <input
                type="text"
                value={localSecondaryCtaLink}
                onChange={(e) => handleLocalFieldChange('secondaryCtaLink', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="/contact"
              />
            </div>
          </div>
        </div>

        {/* Layout Aligner Display Style selection block */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Presentation Layout Matrix</h3>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Style</label>
            <select
              value={localDisplayStyle}
              onChange={(e) => handleLocalFieldChange('displayStyle', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
            >
              <option value="default">Default Align</option>
              <option value="centered">Centered Aligned View</option>
              <option value="split">Split Layout Columns View</option>
            </select>
          </div>
        </div>

      </div>
    </div>
  );
};

export default HeroSection;